import type { FieldValues, UseFormReturn } from "react-hook-form"

// Shared `formProps` defaults for `useHookFormAction` (and `useForm`) so every
// form in the app uses the same validation rhythm.
//
// `mode: "onSubmit"` — the first validation pass happens when the user submits.
// `reValidateMode: "onBlur"` — once a field has errored, it re-validates on
// blur (not on every keystroke), giving "did I fix it?" feedback without
// real-time second-guessing.
//
// Spread into `formProps` alongside the per-form `defaultValues`:
//   formProps: { defaultValues, ...defaultFormValidation }
//
// Override only with a real reason (password strength meter → `mode: "onChange"`;
// async uniqueness check that must run pre-submit → `mode: "onBlur"`).
export const defaultFormValidation = {
  mode: "onSubmit",
  reValidateMode: "onBlur",
} as const

// The next-safe-action RHF adapter maps only `validationErrors` onto the form
// (`useFormIntegration` in @next-safe-action/adapter-react-hook-form);
// `result.serverError` is never mapped, so a form that doesn't wire it up
// fails silently when the action throws. Call this from `actionProps.onError`:
//
//   onError: ({ error }) => {
//     setRootServerError(form, error) // block body: an expression body makes
//   },                                // `form`'s inferred type circular (TS7022)
//
// and render `form.formState.errors.root?.serverError` in the form body.
// RHF clears root errors on the next submit, so the message resets on retry.
export const setRootServerError = <TFieldValues extends FieldValues>(
  form: UseFormReturn<TFieldValues>,
  error: { serverError?: string },
) => {
  if (error.serverError) {
    form.setError("root.serverError", { message: error.serverError })
  }
}
